import React, {
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Center,
  Button,
  Input,
  Spinner,
  Pressable,
  useToast,
  Image,
} from "native-base";
import { StyleSheet, Platform, View, Share } from "react-native";

import { GlobalStyles } from "../../constants/styles";
import { useSelector } from "react-redux";
import axios from "axios";
import Toast from "../../components/Toast";
import { GlobalConstants } from "../../constants/constants";


// const buildApiUrl = (baseUrl, port) => {
//   const cleanBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
//   return `${cleanBaseUrl}:${port}`;
// };

// const LOYALTY_API_BASE_URL = buildApiUrl(
//   GlobalConstants.servicesBaseUrl,
//   "3005"
// );
const LOYALTY_API_BASE_URL = `${GlobalConstants.LoyaltyBaseUrl}`.replace(/\/$/, "");

const makeApiCall = async (
  method,
  endpoint,
  data = null,
  token,
  retries = 2
) => {
  const url = `${LOYALTY_API_BASE_URL}${endpoint}`;
  const config = {
    method,
    url,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    timeout: 30000,
  };
  if (data && ["POST", "PUT", "PATCH"].includes(method)) config.data = data;
  else if (data && method === "GET") config.params = data;
  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (
      retries > 0 &&
      (error.code === "ECONNABORTED" || error.message.includes("timeout"))
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return makeApiCall(method, endpoint, data, token, retries - 1);
    }
    throw error;
  }
};

const safeNum = (v, fallback = 0) => {
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : fallback;
};

const is404 = (err) => err?.response?.status === 404;

const unwrap = (res) =>
  res && typeof res === "object" && "status" in res
    ? res
    : { status: true, data: res };

const formatPoints = (val, fallback = "0.00") => {
  const n = safeNum(String(val ?? "").trim(), NaN);
  return Number.isFinite(n) ? n.toFixed(2) : fallback;
};

const pointsApi = {
  getMyPointsSummary: (token) =>
    makeApiCall("GET", "/api/configuration/user-loyalty-points", null, token),
  redeem: (token, pointsToRedeem, qrCodeData = null) => {
    const body = { points_to_redeem: safeNum(pointsToRedeem, NaN) };
    if (qrCodeData) body.qr_code_data = String(qrCodeData);
    return makeApiCall("POST", "/api/points/redeem", body, token);
  },
  gift: (token, pointsToGift, recipientIdentifier) =>
    makeApiCall(
      "POST",
      "/api/points/gift",
      {
        points_to_gift: safeNum(pointsToGift, NaN),
        recipient_user_id: recipientIdentifier,
      },
      token
    ),
  getTierInfo: (token) =>
    makeApiCall("GET", "/api/configuration/tier-info", null, token),
  // Documented and ready to use as-is.
  generateQR: (token, pointsAmount) =>
    makeApiCall(
      "POST",
      "/api/points/generate-qr",
      { points_amount: safeNum(pointsAmount, NaN) },
      token
    ),
  
  getOrCreateCustomerId: (token) =>
    makeApiCall("POST", "/api/points/customer-id", {}, token),
  getPointsByCustomerId: (token, customerId) =>
    makeApiCall(
      "GET",
      `/api/points/customer/${encodeURIComponent(customerId)}`,
      null,
      token
    ),
  
  getRedeemHistory: (token) =>
    makeApiCall("GET", "/api/points/redeem/history", null, token),
};


const extractPointsFromCustomerResponse = (resData) => {
  const d = resData || {};
  const candidates = [
    d.points_available,
    d.pointsAvailable,
    d.available_points,
    d.availablePoints,
    d.points,
    d?.loyaltyPoints?.points_available,
    d?.data?.points_available,
  ];
  const n = candidates
    .map((x) => safeNum(x, NaN))
    .find((x) => Number.isFinite(x));
  return Number.isFinite(n) ? n : null;
};

const extractCustomerId = (resData) => {
  if (!resData) return null;
  if (typeof resData === "string") return resData;
  return (
    resData.customer_id ||
    resData.customerId ||
    resData?.data?.customer_id ||
    null
  );
};

const pageBgStyle = { backgroundColor: GlobalStyles.colors.lightGrey };

const cardShadow = {
  shadowColor: "#000000",
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.25,
  shadowRadius: 4,
  elevation: 4,
  boxShadow: "0px 4px 4px 0px #00000040",
};

const gradientBg = {
  background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
  backgroundColor: GlobalStyles.colors.blue,
};

const FONT = GlobalStyles.text.fontFamily;
const saveQrToDevice = async (dataUri, filename = "loyalty-qr.png") => {
  if (Platform.OS === "web") {
    const link = document.createElement("a");
    link.href = dataUri;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return { ok: true, method: "download" };
  }

  let MediaLibrary = null;
  let FileSystem = null;
  try { MediaLibrary = require("expo-media-library"); } catch (_) { MediaLibrary = null; }
  try { FileSystem = require("expo-file-system"); } catch (_) { FileSystem = null; }

  if (MediaLibrary && FileSystem) {
    try {
      const { status } = await MediaLibrary.requestPermissionsAsync();
      if (status !== "granted") {
        return {
          ok: false,
          error: new Error(
            "Photos permission was denied — enable it in device Settings to save QR codes.",
          ),
        };
      }
      const base64 = String(dataUri).replace(/^data:image\/\w+;base64,/, "");
      const fileUri = `${FileSystem.cacheDirectory}${filename}`;
      await FileSystem.writeAsStringAsync(fileUri, base64, {
        encoding: FileSystem.EncodingType.Base64,
      });
      const asset = await MediaLibrary.createAssetAsync(fileUri);
      await MediaLibrary.createAlbumAsync("TownTicks", asset, false).catch(() => {});
      return { ok: true, method: "gallery" };
    } catch (e) {
    }
  }

  try {
    await Share.share({
      url: dataUri, 
      message: Platform.OS === "android" ? dataUri : undefined,
      title: "Save your TownTicks QR code",
    });
    return { ok: true, method: "share" };
  } catch (e) {
    return { ok: false, error: e };
  }
};

const RedeemRow = ({ label, value, isLoading }) => (
  <View
    style={{
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    }}
  >
    <Text
      style={{
        fontFamily: FONT,
        fontSize: 14,
        fontWeight: "400",
        color: "#000000",
        flex: 1,
        marginRight: 12,
      }}
    >
      {label}
    </Text>

    <View
      style={{
        width: 180,
        height: 34,
        borderWidth: 1,
        borderColor: "#000000",
        backgroundColor: "#FFFFFF",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {isLoading ? (
        <Spinner size="sm" color={GlobalStyles.colors.blue} />
      ) : (
        <Text
          style={{
            fontFamily: FONT,
            fontSize: 14,
            color: "#000000",
            textAlign: "center",
          }}
        >
          {value}
        </Text>
      )}
    </View>
  </View>
);

// Explainer copy used on both tabs.
const InfoCallout = ({ children }) => (
  <View
    style={{
      width: "100%",
      backgroundColor: "#EAF1FF",
      borderLeftWidth: 3,
      borderLeftColor: GlobalStyles.colors.blue,
      borderRadius: 4,
      paddingHorizontal: 14,
      paddingVertical: 10,
      marginBottom: 16,
    }}
  >
    <Text
      style={{
        fontFamily: FONT,
        fontSize: 12.5,
        lineHeight: 17,
        color: "#28304A",
      }}
    >
      {children}
    </Text>
  </View>
);

const RedeemPointsCitizen = ({ toast, user }) => {
  const [isLoading, setIsLoading]         = useState(true);
  const [isRedeeming, setIsRedeeming]     = useState(false);
  const [currentPoints, setCurrentPoints] = useState("xx");
  const [currentTier, setCurrentTier]     = useState("xx");
  const [tillNextTier, setTillNextTier]   = useState("xx");
  const [totalRedeemed, setTotalRedeemed] = useState("0.00");
  const [redeemAmount, setRedeemAmount]   = useState("");

  const [isHistoryOpen, setIsHistoryOpen]         = useState(false);
  const [isHistoryLoading, setIsHistoryLoading]   = useState(false);
  const [historyItems, setHistoryItems]           = useState(null);
  const [historyApiAvailable, setHistoryApiAvailable] = useState(true);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [qrImageUri, setQrImageUri]         = useState(null);
  const [isSavingQR, setIsSavingQR]         = useState(false);

  const [myCustomerId, setMyCustomerId] = useState(null);

  const loadData = useCallback(async () => {
    if (!user?.token) return;
    setIsLoading(true);
    try {
      const [loyaltyRaw, tierRaw] = await Promise.all([
        pointsApi.getMyPointsSummary(user.token).catch(() => null),
        pointsApi.getTierInfo(user.token).catch(() => null),
      ]);

      if (loyaltyRaw) {
        const res = unwrap(loyaltyRaw);
        if (res.status && res.data) {
          const lp = res.data.loyaltyPoints || res.data;
          setCurrentPoints(formatPoints(lp.points_available ?? lp.points));
          setTotalRedeemed(formatPoints(lp.points_redeemed ?? 0));
        }
      }

      if (tierRaw) {
        const res = unwrap(tierRaw);
        if (res.status && res.data) {
          const d = res.data;
          const tierName =
            d.current_tier_name ||
            d.tier_name ||
            d.current_tier ||
            d.tier_level ||
            "—";
          setCurrentTier(String(tierName));
          const till =
            d.points_till_next_tier ??
            d.points_to_next_tier ??
            d.next_tier_points_needed ??
            null;
          setTillNextTier(till !== null ? formatPoints(till) : "—");
        }
      }
    } catch (err) {
      console.error("RedeemPointsCitizen loadData:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.token]);

  const resolveCustomerId = useCallback(async () => {
    if (!user?.token) return;
    try {
      const cidRaw = await pointsApi.getOrCreateCustomerId(user.token);
      const cidRes = unwrap(cidRaw);
      const resolvedId = cidRes.status ? extractCustomerId(cidRes.data) : null;
      if (!resolvedId) return;
      setMyCustomerId(resolvedId);

      const custRaw = await pointsApi
        .getPointsByCustomerId(user.token, resolvedId)
        .catch(() => null);
      if (custRaw) {
        const custRes = unwrap(custRaw);
        if (custRes.status) {
          const pts = extractPointsFromCustomerResponse(custRes.data);
          if (pts !== null) setCurrentPoints(formatPoints(pts));
        }
      }
    } catch (cidErr) {
      console.warn(
        "customer_id resolution skipped (endpoint not available yet):",
        cidErr?.message,
      );
    }
  }, [user?.token]);

  useEffect(() => {
    loadData();
    resolveCustomerId();
  }, [loadData, resolveCustomerId]);


  const handleToggleHistory = async () => {
    const opening = !isHistoryOpen;
    setIsHistoryOpen(opening);
    if (opening && historyItems === null && user?.token) {
      setIsHistoryLoading(true);
      try {
        const raw = await pointsApi.getRedeemHistory(user.token);
        const res = unwrap(raw);
        const items = res?.data?.items || res?.data?.history || res?.data;
        if (res.status && Array.isArray(items)) {
          setHistoryItems(items);
        } else {
          setHistoryApiAvailable(false);
          setHistoryItems([]);
        }
      } catch (err) {
        setHistoryApiAvailable(false);
        setHistoryItems([]);
      } finally {
        setIsHistoryLoading(false);
      }
    }
  };

  const handleRedeem = async () => {
    const pts = safeNum(String(redeemAmount).replace(/[^0-9.]/g, ""), NaN);
    if (!Number.isFinite(pts) || pts <= 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a valid points amount to redeem"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    const available = safeNum(currentPoints, 0);
    if (pts > available) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={`Insufficient points. You have ${currentPoints} points available.`}
          />
        ),
        placement: "top",
        duration: 4000,
      });
      return;
    }
    setIsRedeeming(true);
    try {
      const res = unwrap(await pointsApi.redeem(user.token, pts));
      if (!res.status) throw new Error(res.status_msg || "Redeem failed");
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={`${formatPoints(pts)} points redeemed successfully!`}
          />
        ),
        placement: "top",
        duration: 3000,
      });
      setRedeemAmount("");
      setHistoryItems(null); 
      await loadData();
    } catch (error) {
      const msg =
        error?.response?.data?.status_msg ||
        error?.message ||
        "Failed to redeem points.";
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 5000,
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleGenerateQR = async () => {
    const pts = safeNum(String(redeemAmount).replace(/[^0-9.]/g, ""), NaN);
    if (!Number.isFinite(pts) || pts <= 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Enter a points amount above first, then generate a QR code for it"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    try {
      setIsGeneratingQR(true);
      const rawResponse = await pointsApi.generateQR(user.token, pts);
      const res = unwrap(rawResponse);
      console.log("generate-qr raw response:", rawResponse);

      if (!res.status) {
        throw new Error(res.status_msg || "Server rejected the QR request");
      }

      const d = res.data || {};
      const raw =
        d.qr_code ||
        d.qr_image ||
        d.qrImage ||
        d.qrCode ||
        d.qr_code_image ||
        d.image ||
        d.qr ||
        d.qr_data ||
        d.qrData ||
        d.dataUrl ||
        d.data_url ||
        d.base64 ||
        d.data ||
        (typeof d === "string" ? d : null) ||
        (typeof res.data === "string" ? res.data : null);

      if (!raw) {
        console.warn(
          "generate-qr succeeded but no recognizable image field was found in:",
          d,
        );
        throw new Error(
          "QR code was generated but the image field wasn't recognized — check the console log for the raw response.",
        );
      }

      const uri = String(raw).startsWith("data:")
        ? raw
        : `data:image/png;base64,${raw}`;
      setQrImageUri(uri);
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.green} message="QR code generated!" />
        ),
        placement: "top",
        duration: 2000,
      });
    } catch (error) {
      const msg =
        error?.response?.data?.status_msg ||
        error?.message ||
        "Failed to generate QR code";
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 4000,
      });
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const handleSaveQR = async () => {
    if (!qrImageUri) return;
    setIsSavingQR(true);
    try {
      const result = await saveQrToDevice(
        qrImageUri,
        `townticks-qr-${Date.now()}.png`,
      );
      if (result.ok) {
        const message =
          result.method === "download"
            ? "QR code downloaded!"
            : result.method === "gallery"
            ? "Saved to your Photos!"
            : "Choose 'Save Image' to keep it on your phone.";
        toast.show({
          render: () => (
            <Toast color={GlobalStyles.colors.green} message={message} />
          ),
          placement: "top",
          duration: 3000,
        });
      } else {
        throw result.error || new Error("Could not save QR code");
      }
    } catch (error) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={error?.message || "Could not save QR code"}
          />
        ),
        placement: "top",
        duration: 3000,
      });
    } finally {
      setIsSavingQR(false);
    }
  };

  return (
    <Center flex={1} style={pageBgStyle} px={4}>
      <Box w="100%" maxW="600px" alignSelf="center" pt="20px" pb="24px">

        <InfoCallout>
          TT Points are TownTicks' shared loyalty points. When a business
          joins the TT common pool, points you earn there can be redeemed
          more broadly across participating businesses — and gifted to
          friends and family too.
        </InfoCallout>

        <View
          style={[
            {
              backgroundColor: "#FFFFFF",
              borderRadius: 12,
              paddingHorizontal: 24,
              paddingTop: 20,
              paddingBottom: 20,
              width: "100%",
            },
            cardShadow,
          ]}
        >
          <VStack space={3} w="100%">
            <RedeemRow
              label="Your Current points:"
              value={currentPoints}
              isLoading={isLoading}
            />
            <RedeemRow
              label="Your current Tier:"
              value={currentTier}
              isLoading={isLoading}
            />
            <RedeemRow
              label="Points Till Next Tier:"
              value={tillNextTier}
              isLoading={isLoading}
            />

            <View style={{ width: "100%" }}>
              <Pressable onPress={handleToggleHistory}>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Text
                    style={{
                      fontFamily: FONT,
                      fontSize: 14,
                      fontWeight: "400",
                      color: "#000000",
                      flex: 1,
                      marginRight: 12,
                    }}
                  >
                    Redeem History
                  </Text>
                  <View
                    style={{
                      width: 180,
                      height: 34,
                      borderWidth: 1,
                      borderColor: GlobalStyles.colors.blue,
                      backgroundColor: "#FFFFFF",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "row",
                    }}
                  >
                    <Text
                      style={{
                        fontFamily: FONT,
                        fontSize: 14,
                        color: GlobalStyles.colors.blue,
                        fontWeight: "bold",
                      }}
                    >
                      {isHistoryOpen ? "Hide ▲" : "View ▼"}
                    </Text>
                  </View>
                </View>
              </Pressable>

              {isHistoryOpen && (
                <View
                  style={{
                    width: "100%",
                    marginTop: 8,
                    borderWidth: 1,
                    borderColor: "#EEEEEE",
                    borderRadius: 6,
                    padding: 12,
                    backgroundColor: "#FAFAFA",
                  }}
                >
                  {isHistoryLoading ? (
                    <Spinner size="sm" color={GlobalStyles.colors.blue} />
                  ) : historyApiAvailable && historyItems && historyItems.length > 0 ? (
                    <VStack space={2}>
                      {historyItems.map((item, i) => (
                        <HStack
                          key={item.id ?? i}
                          justifyContent="space-between"
                          w="100%"
                        >
                          <Text style={{ fontFamily: FONT, fontSize: 12.5, color: "#333" }}>
                            {item.date || item.created_at || "—"}
                          </Text>
                          <Text style={{ fontFamily: FONT, fontSize: 12.5, fontWeight: "bold", color: "#000" }}>
                            {formatPoints(item.points ?? item.points_redeemed)} pts
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  ) : (
                    <VStack space={1}>
                      <Text
                        style={{
                          fontFamily: FONT,
                          fontSize: 13,
                          fontWeight: "bold",
                          color: "#000",
                        }}
                      >
                        Total redeemed to date: {totalRedeemed} pts
                      </Text>
                      {!historyApiAvailable && (
                        <Text
                          style={{
                            fontFamily: FONT,
                            fontSize: 11.5,
                            color: "#888",
                          }}
                        >
                          Couldn't load itemized history right now — showing
                          your running total only.
                        </Text>
                      )}
                    </VStack>
                  )}
                </View>
              )}
            </View>

            <View
              style={{
                width: "100%",
                height: 1,
                backgroundColor: "#EEEEEE",
                marginVertical: 2,
              }}
            />

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Text
                style={{
                  fontFamily: FONT,
                  fontSize: 14,
                  fontWeight: "400",
                  color: "#000000",
                  flex: 1,
                  marginRight: 12,
                }}
              >
                Enter Points to Redeem:
              </Text>
              <View
                style={{
                  width: 180,
                  height: 34,
                  borderWidth: 1,
                  borderColor: GlobalStyles.colors.blue,
                  backgroundColor: "#FFFFFF",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Input
                  value={redeemAmount}
                  onChangeText={(v) => {
                    setRedeemAmount(v);
                    setQrImageUri(null); 
                  }}
                  variant="unstyled"
                  fontSize={14}
                  color="#000000"
                  keyboardType="numeric"
                  placeholder="0"
                  textAlign="center"
                  style={{
                    fontFamily: FONT,
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                  }}
                  p={0}
                />
              </View>
            </View>

            <HStack space={3} justifyContent="center" mt={2}>
              <Button
                w="150px"
                h="38px"
                borderRadius={6}
                bg={GlobalStyles.colors.blue}
                onPress={handleRedeem}
                isLoading={isRedeeming}
                isLoadingText="Redeeming..."
                isDisabled={!redeemAmount || isLoading}
                style={gradientBg}
                _text={{
                  color: "#FFFFFF",
                  fontFamily: FONT,
                  fontWeight: "bold",
                  fontSize: 14,
                }}
              >
                Redeem Points
              </Button>
              <Button
                w="150px"
                h="38px"
                borderRadius={6}
                variant="outline"
                borderColor={GlobalStyles.colors.blue}
                onPress={handleGenerateQR}
                isLoading={isGeneratingQR}
                isLoadingText="Generating..."
                isDisabled={!redeemAmount || isLoading}
                _text={{
                  color: GlobalStyles.colors.blue,
                  fontFamily: FONT,
                  fontWeight: "bold",
                  fontSize: 14,
                }}
              >
                Generate QR
              </Button>
            </HStack>

            {qrImageUri && (
              <VStack alignItems="center" space={2} mt={2}>
                <Image
                  source={{ uri: qrImageUri }}
                  alt="Redeem QR Code"
                  w="160px"
                  h="160px"
                  resizeMode="contain"
                />
                {myCustomerId && (
                  <Text
                    style={{
                      fontFamily: FONT,
                      fontSize: 11.5,
                      color: "#888",
                    }}
                  >
                    Customer ID: {myCustomerId}
                  </Text>
                )}
                <Button
                  w="180px"
                  h="34px"
                  borderRadius={6}
                  bg={GlobalStyles.colors.blue}
                  onPress={handleSaveQR}
                  isLoading={isSavingQR}
                  isLoadingText="Saving..."
                  _text={{
                    color: "#FFFFFF",
                    fontFamily: FONT,
                    fontWeight: "bold",
                    fontSize: 13,
                  }}
                >
                  Save QR to my phone
                </Button>
              </VStack>
            )}
          </VStack>
        </View>
      </Box>
    </Center>
  );
};

const GiftingPointsCitizen = ({ toast, user }) => {
  const [isLoading, setIsLoading]           = useState(true);
  const [isGifting, setIsGifting]           = useState(false);
  const [currentPoints, setCurrentPoints]   = useState("0.00");
  const [recipientInput, setRecipientInput] = useState("");
  const [giftAmount, setGiftAmount]         = useState("");
  const [confirmed, setConfirmed]           = useState(false);
  const [successMsg, setSuccessMsg]         = useState("");

  const loadPoints = useCallback(async () => {
    if (!user?.token) return;
    setIsLoading(true);
    try {
      const res = unwrap(await pointsApi.getMyPointsSummary(user.token));
      if (res.status && res.data) {
        const lp = res.data.loyaltyPoints || res.data;
        setCurrentPoints(
          formatPoints(lp.points_available ?? lp.points, "0.00")
        );
      }
    } catch (err) {
      console.error("GiftingPointsCitizen loadPoints:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    loadPoints();
  }, [loadPoints]);

  const recipientDisplay = recipientInput.trim() || "XYZ";
  const giftAmountNum = safeNum(String(giftAmount).replace(/[^0-9.]/g, ""), NaN);
  const availableNum = safeNum(currentPoints, 0);
  const exceedsAvailable =
    Number.isFinite(giftAmountNum) && giftAmountNum > availableNum;

  const handleFirstConfirm = () => {
    if (!recipientInput.trim()) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter your friend's TownTicks username or email"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    setConfirmed(true);
  };

  const handleGift = async () => {
    const pts = giftAmountNum;
    if (!Number.isFinite(pts) || pts <= 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a valid amount to gift"
          />
        ),
        placement: "top",
        duration: 3000,
      });
      return;
    }
    if (pts > availableNum) {
      
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={`Insufficient points. You have ${currentPoints} points available.`}
          />
        ),
        placement: "top",
        duration: 4000,
      });
      return;
    }
    setIsGifting(true);
    setSuccessMsg("");
    try {
      const res = unwrap(
        await pointsApi.gift(user.token, pts, recipientInput.trim())
      );
      if (!res.status) throw new Error(res.status_msg || "Gift failed");
      const msg = `${formatPoints(pts)} TT points have been given to your friend ${recipientDisplay}`;
      setSuccessMsg(msg);
      toast.show({
        render: () => (
          <Toast color={GlobalStyles.colors.green} message={msg} />
        ),
        placement: "top",
        duration: 3000,
      });
      setRecipientInput("");
      setGiftAmount("");
      setConfirmed(false);
      await loadPoints();
    } catch (error) {
      const msg =
        error?.response?.data?.status_msg ||
        error?.message ||
        "Failed to gift points.";
      toast.show({
        render: () => <Toast color={GlobalStyles.colors.red} message={msg} />,
        placement: "top",
        duration: 5000,
      });
    } finally {
      setIsGifting(false);
    }
  };

  const ConfirmBtn = ({ onPress, disabled, isProcessing }) => (
    <Pressable
      onPress={onPress}
      disabled={disabled || isProcessing}
      style={{ opacity: disabled || isProcessing ? 0.45 : 1 }}
    >
      <View
        style={{
          height: 40,
          minWidth: 100,
          borderRadius: 4,
          alignItems: "center",
          justifyContent: "center",
          paddingHorizontal: 22,
          background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
          backgroundColor: GlobalStyles.colors.blue,
        }}
      >
        <Text
          style={{
            fontFamily: FONT,
            fontWeight: "bold",
            fontSize: 15,
            color: "#FFFFFF",
          }}
        >
          {isProcessing ? "..." : "Confirm"}
        </Text>
      </View>
    </Pressable>
  );

  const finalConfirmDisabled =
    !giftAmount ||
    !confirmed ||
    isLoading ||
    !Number.isFinite(giftAmountNum) ||
    giftAmountNum <= 0 ||
    exceedsAvailable;

  return (
    <Center flex={1} style={pageBgStyle} px={4}>
      <Box w="100%" maxW="600px" alignSelf="center" pt="28px" pb="36px">

        <Text
          style={{
            fontFamily: FONT,
            fontSize: 22,
            fontWeight: "700",
            color: "#000000",
            marginBottom: 14,
          }}
        >
          Gifting Points – Citizen User
        </Text>

        <InfoCallout>
          Gifting sends TT Points straight from your balance to a friend's
          TownTicks account. Because TT Points are pooled across every
          business that's joined the common pool, your friend can redeem
          them anywhere in that pool — not just where you earned them.
        </InfoCallout>

        <View
          style={{
            width: "100%",
            borderWidth: 1,
            borderColor: "#BDBDBD",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <View
            style={{
              backgroundColor: "#D9D9D9",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingHorizontal: 20,
              paddingVertical: 13,
              borderBottomWidth: 1,
              borderBottomColor: "rgba(0,0,0,0.12)",
            }}
          >
            <Text
              style={{
                fontFamily: FONT,
                fontSize: 14,
                fontWeight: "400",
                color: "#000000",
              }}
            >
              Your Current TownTicks Points:
            </Text>

            <View
              style={{
                borderWidth: 2,
                borderColor: GlobalStyles.colors.blue,
                backgroundColor: "#FFFFFF",
                minWidth: 52,
                height: 40,
                borderRadius: 2,
                alignItems: "center",
                justifyContent: "center",
                paddingHorizontal: 12,
              }}
            >
              {isLoading ? (
                <Spinner size="sm" color={GlobalStyles.colors.blue} />
              ) : (
                <Text
                  style={{
                    fontFamily: FONT,
                    fontWeight: "bold",
                    fontSize: 16,
                    color: GlobalStyles.colors.blue,
                  }}
                >
                  {currentPoints}
                </Text>
              )}
            </View>
          </View>

          <View
            style={{
              backgroundColor: "rgba(217,217,217,0.25)",
              paddingHorizontal: 20,
              paddingTop: 18,
              paddingBottom: 10,
            }}
          >
            <Text
              style={{
                fontFamily: FONT,
                fontSize: 14,
                color: "#000000",
                marginBottom: 8,
              }}
            >
              Enter Friend TownTicks username or email
            </Text>
            <View
              style={{
                width: "100%",
                height: 44,
                borderWidth: 1,
                borderColor: "#CCCCCC",
                backgroundColor: "#FFFFFF",
                borderRadius: 4,
                justifyContent: "center",
                paddingHorizontal: 10,
                marginBottom: 18,
              }}
            >
              <Input
                value={recipientInput}
                onChangeText={(v) => {
                  setRecipientInput(v);
                  if (confirmed) setConfirmed(false);
                  if (successMsg) setSuccessMsg("");
                }}
                variant="unstyled"
                fontSize={14}
                color="#000000"
                keyboardType="email-address"
                autoCapitalize="none"
                placeholder=""
                style={{ fontFamily: FONT, width: "100%" }}
                p={0}
              />
            </View>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 14,
              }}
            >
              <Text
                style={{
                  fontFamily: FONT,
                  fontSize: 14,
                  color: "#000000",
                  flex: 1,
                  marginRight: 12,
                }}
              >
                Gift TT points to your friend {recipientDisplay}?
              </Text>
              <ConfirmBtn
                onPress={handleFirstConfirm}
                disabled={confirmed || !recipientInput.trim()}
              />
            </View>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <Text
                style={{
                  fontFamily: FONT,
                  fontSize: 14,
                  color: "#000000",
                  flex: 1,
                  marginRight: 12,
                }}
              >
                How much TT points to give?
              </Text>

              <View
                style={{
                  minWidth: 72,
                  height: 40,
                  borderRadius: 2,
                  alignItems: "center",
                  justifyContent: "center",
                  paddingHorizontal: 10,
                  background: "linear-gradient(90deg, #000561 0%, #000AC7 100%)",
                  backgroundColor: exceedsAvailable ? "#C62828" : GlobalStyles.colors.blue,
                }}
              >
                <Input
                  value={giftAmount}
                  onChangeText={setGiftAmount}
                  variant="unstyled"
                  textAlign="center"
                  fontSize={16}
                  fontWeight="bold"
                  color="#FFFFFF"
                  keyboardType="numeric"
                  placeholder="0"
                  placeholderTextColor="rgba(255,255,255,0.55)"
                  style={{
                    fontFamily: FONT,
                    width: 56,
                    textAlign: "center",
                    color: "#FFFFFF",
                  }}
                  _focus={{ bg: "transparent" }}
                  p={0}
                  m={0}
                />
              </View>
            </View>

            {exceedsAvailable && (
              <Text
                style={{
                  fontFamily: FONT,
                  fontSize: 12,
                  color: "#C62828",
                  textAlign: "right",
                  marginBottom: 10,
                }}
              >
                That's more than your available {currentPoints} points.
              </Text>
            )}

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
                marginTop: exceedsAvailable ? 0 : 10,
              }}
            >
              <Text
                style={{
                  fontFamily: FONT,
                  fontSize: 14,
                  color: "#000000",
                  flex: 1,
                  marginRight: 12,
                }}
              >
                You selected "{giftAmount || "R"}" TT points for gift to {recipientDisplay}?
              </Text>
              <ConfirmBtn
                onPress={handleGift}
                disabled={finalConfirmDisabled}
                isProcessing={isGifting}
              />
            </View>
          </View>
        </View>

        <View
          style={{
            alignSelf: "center",
            marginTop: 18,
            backgroundColor: "#FFFFFF",
            borderWidth: 1,
            borderColor: "#CCCCCC",
            borderRadius: 4,
            paddingHorizontal: 28,
            paddingVertical: 13,
            boxShadow: "0px 4px 4px 0px #00000040",
            shadowColor: "#000000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.25,
            shadowRadius: 4,
            elevation: 4,
          }}
        >
          <Text
            style={{
              fontFamily: FONT,
              fontSize: 14,
              color: successMsg ? "#000000" : "#9E9E9E",
              textAlign: "center",
            }}
          >
            {successMsg
              ? successMsg
              : `X TT points have been given to your friend ${recipientDisplay}?`}
          </Text>
        </View>
      </Box>
    </Center>
  );
};

const CitizenLoyaltyManager = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState("redeem");
  const toast = useToast();
  const user  = useSelector((state) => state.user.user);

  const getTabColor = (tab) =>
    activeTab === tab
      ? GlobalStyles.colors.mustard
      : GlobalStyles.colors.blue;

  useEffect(() => {
    if (user?.token)
      axios.defaults.headers.common.Authorization = `Bearer ${user.token}`;
  }, [user]);

  return (
    <Box
      w="100%"
      bg={GlobalStyles.colors.lightGrey}
      minH={Platform.OS === "web" ? "100vh" : "100%"}
    >
      <Center>
        <Box w={["92%", 600, 860]} maxW="960px">
          {onBack && (
            <HStack w="100%" mb={3} px={4} mt={4}>
              <Pressable onPress={onBack}>
                <Text
                  color={GlobalStyles.colors.blue}
                  fontWeight="500"
                  fontSize={14}
                  fontFamily={FONT}
                >
                  ← Back to Services
                </Text>
              </Pressable>
            </HStack>
          )}

          <HStack space={2} mb={6} mt={5} justifyContent="center" flexWrap="wrap">
            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={getTabColor("redeem")}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => setActiveTab("redeem")}
            >
              <Text
                fontWeight="600"
                fontFamily={FONT}
                color={getTabColor("redeem")}
              >
                Points Status
              </Text>
            </Button>

            <Button
              size="sm"
              mx={1}
              my={1}
              variant="solid"
              px={4}
              bg={GlobalStyles.colors.white}
              borderColor={getTabColor("gift")}
              borderWidth={2}
              borderRadius={40}
              _hover={{ bg: GlobalStyles.colors.grey }}
              onPress={() => setActiveTab("gift")}
            >
              <Text
                fontWeight="600"
                fontFamily={FONT}
                color={getTabColor("gift")}
              >
                Gifting Points
              </Text>
            </Button>
          </HStack>
        </Box>
      </Center>

      <Box pb="48px">
        {activeTab === "redeem" && (
          <RedeemPointsCitizen toast={toast} user={user} />
        )}
        {activeTab === "gift" && (
          <GiftingPointsCitizen toast={toast} user={user} />
        )}
      </Box>
    </Box>
  );
};

export default CitizenLoyaltyManager;